import { MonitoringOptionsFixed } from '../pages/MLOps/Monitoring';
import { OneOf } from '../pages/Orchestrator/WorkflowTabCanvas';
import { fetchWrapRPC, fetchWrapRPCRawResponse } from '../services/api';
import {
  CancelJobRequest,
  CreateJobRequest,
  DeleteJobRequest,
  GetJobOutputRequest,
  GetJobRequest,
  JobService_CancelJob,
  JobService_CreateJob,
  JobService_DeleteJob,
  JobService_GetJob,
  JobService_GetJobOutput,
  JobService_ListJobs,
  ListJobsRequest,
} from './gen/ai/h2o/mlops/batch/v1/job_service_pb';
import { ListSinkSpecsRequest, SinkSpecService_ListSinkSpecs } from './gen/ai/h2o/mlops/batch/v1/sink_spec_service_pb';
import {
  ListSourceSpecsRequest,
  SourceSpecService_ListSourceSpecs,
} from './gen/ai/h2o/mlops/batch/v1/source_spec_service_pb';
import {
  ArtifactCompositionService_ListExperimentArtifactCompositions,
  ListExperimentArtifactCompositionsRequest,
} from './gen/ai/h2o/mlops/deployer/v2/artifact_composition_service_pb';
import {
  ConfigService_DiscoverDefaultRuntimeKubernetesResourceRequirements,
  ConfigService_DiscoverKubernetesConfigShortcuts,
  ConfigService_GetDefaultSecurityOptionType,
  ConfigService_ListSecurityOptionTypes,
  DiscoverDefaultRuntimeKubernetesResourceRequirementsRequest,
  DiscoverKubernetesConfigShortcutsRequest,
  GetDefaultSecurityOptionTypeRequest,
  ListSecurityOptionTypesRequest,
} from './gen/ai/h2o/mlops/deployer/v2/config_service_pb';
import {
  BatchGetDeploymentsMetadataRequest,
  DeploymentMetadataService_BatchGetDeploymentsMetadata,
  DeploymentMetadataService_GetDeploymentMetadata,
  GetDeploymentMetadataRequest,
} from './gen/ai/h2o/mlops/deployer/v2/deployment_metadata_service_pb';
import {
  Deployment,
  ShadowDeployment,
  SingleDeployment,
  SplitDeployment,
} from './gen/ai/h2o/mlops/deployer/v2/deployment_pb';
import {
  ComputeSuggestedScoringResourceRequirementsRequest,
  DeploymentProfilingService_ComputeSuggestedScoringResourceRequirements,
} from './gen/ai/h2o/mlops/deployer/v2/deployment_profiling_service_pb';
import {
  CreateDeploymentRequest,
  DeleteDeploymentRequest,
  DeploymentService_CreateDeployment,
  DeploymentService_DeleteDeployment,
  DeploymentService_GetDeployment,
  DeploymentService_ListDeployments,
  DeploymentService_UpdateDeployment,
  GetDeploymentRequest,
  ListDeploymentsRequest,
  ListDeploymentsResponse,
  UpdateDeploymentRequest,
} from './gen/ai/h2o/mlops/deployer/v2/deployment_service_pb';
import {
  BatchGetDeploymentStatusesRequest,
  DeploymentStatusService_BatchGetDeploymentStatuses,
  DeploymentStatusService_GetDeploymentStatus,
  GetDeploymentStatusRequest,
} from './gen/ai/h2o/mlops/deployer/v2/deployment_status_service_pb';
import { GenerateLogsRequest, LogService_GenerateLogs } from './gen/ai/h2o/mlops/deployer/v2/log_service_pb';
import { RPC } from './gen/runtime';

// TODO: Remove basePath modification once generated ts api is fixed.
const BATCH_PREFIX = '/batch'; // Previously '/deploymentServer/batch';
const DEPLOYER_PREFIX = '/deployer';

type DeploymentWithCorrectMontoringOptions = Omit<Deployment, 'options'> & {
  monitoringOptions: MonitoringOptionsFixed;
};
export type DeploymentFixed = DeploymentWithCorrectMontoringOptions &
  OneOf<{
    single: SingleDeployment;
    shadow: ShadowDeployment;
    split: SplitDeployment;
  }> &
  Pick<Required<Deployment>, `name` | `etag`>;

export type DeploymentMode =
  | {
      type: `single`;
      params: SingleDeployment;
    }
  | { type: `shadow`; params: ShadowDeployment }
  | { type: `split`; params: SplitDeployment };

export const getDeploymentMode = (deployment: Deployment): DeploymentMode => {
  if (`single` in deployment) {
    return { type: `single`, params: deployment.single! };
  }
  if (`shadow` in deployment) {
    return { type: `shadow`, params: deployment.shadow! };
  }
  if (`split` in deployment) {
    return { type: `split`, params: deployment.split! };
  }
  throw new Error(`Unknown deployment mode: ${JSON.stringify(deployment)}`);
};

export type ListDeploymentsResponseFixed = Omit<ListDeploymentsResponse, 'deployments'> & {
  deployments?: DeploymentFixed[];
};
export type GetDeploymentResponseFixed = Omit<GetDeploymentRequest, 'deployment'> & {
  deployment: DeploymentFixed;
};

export class MLOpsServiceImpl {
  private basePath: string;
  constructor(basePath?: string) {
    this.basePath = basePath || '';
  }

  public setBasePath = (path: string) => {
    this.basePath = path;
  };

  public async listJobs(req: ListJobsRequest) {
    return await fetchWrapRPC(JobService_ListJobs, { basePath: `${this.basePath}${BATCH_PREFIX}` })({ ...req });
  }

  public async deleteJob(req: DeleteJobRequest) {
    return await fetchWrapRPC(JobService_DeleteJob, { basePath: `${this.basePath}${BATCH_PREFIX}` })({
      ...req,
    });
  }

  public async createJob(req: CreateJobRequest) {
    return await fetchWrapRPC(JobService_CreateJob, { basePath: `${this.basePath}${BATCH_PREFIX}` })({
      ...req,
    });
  }

  public async cancelJob(req: CancelJobRequest) {
    return await fetchWrapRPC(JobService_CancelJob, { basePath: `${this.basePath}${BATCH_PREFIX}` })({
      ...req,
    });
  }

  public getJobOutput(jobId: GetJobOutputRequest) {
    return fetchWrapRPCRawResponse(JobService_GetJobOutput, { basePath: `${this.basePath}${BATCH_PREFIX}` })({
      ...jobId,
    });
  }

  public async listSourceSpecs(req: ListSourceSpecsRequest) {
    return await fetchWrapRPC(SourceSpecService_ListSourceSpecs, {
      basePath: `${this.basePath}${BATCH_PREFIX}`,
    })({
      ...req,
    });
  }

  public async listSinkSpecs(req: ListSinkSpecsRequest) {
    return await fetchWrapRPC(SinkSpecService_ListSinkSpecs, {
      basePath: `${this.basePath}${BATCH_PREFIX}`,
    })({
      ...req,
    });
  }

  public async getJob(req: GetJobRequest) {
    return await fetchWrapRPC(JobService_GetJob, { basePath: `${this.basePath}${BATCH_PREFIX}` })({
      ...req,
    });
  }

  public async listDeployments(req: ListDeploymentsRequest) {
    return await fetchWrapRPC(
      DeploymentService_ListDeployments as RPC<ListDeploymentsRequest, ListDeploymentsResponseFixed>,
      { basePath: `${this.basePath}${DEPLOYER_PREFIX}` }
    )({
      ...req,
    });
  }

  public async getDeployment(req: GetDeploymentRequest) {
    return await fetchWrapRPC(
      DeploymentService_GetDeployment as RPC<GetDeploymentRequest, GetDeploymentResponseFixed>,
      {
        basePath: `${this.basePath}${DEPLOYER_PREFIX}`,
      }
    )({
      ...req,
    });
  }

  public async deleteDeployment(req: DeleteDeploymentRequest) {
    return await fetchWrapRPC(DeploymentService_DeleteDeployment, { basePath: `${this.basePath}${DEPLOYER_PREFIX}` })({
      ...req,
    });
  }

  public async createDeployment(req: CreateDeploymentRequest) {
    return await fetchWrapRPC(DeploymentService_CreateDeployment, { basePath: `${this.basePath}${DEPLOYER_PREFIX}` })({
      ...req,
    });
  }

  public async updateDeployment(req: UpdateDeploymentRequest) {
    return await fetchWrapRPC(DeploymentService_UpdateDeployment, { basePath: `${this.basePath}${DEPLOYER_PREFIX}` })({
      ...req,
    });
  }

  public async getDeploymentStatus(req: GetDeploymentStatusRequest) {
    return await fetchWrapRPC(DeploymentStatusService_GetDeploymentStatus, {
      basePath: `${this.basePath}${DEPLOYER_PREFIX}`,
    })({
      ...req,
    });
  }

  public async getDefaultSecurityOptionType(req: GetDefaultSecurityOptionTypeRequest) {
    return await fetchWrapRPC(ConfigService_GetDefaultSecurityOptionType, {
      basePath: `${this.basePath}${DEPLOYER_PREFIX}`,
    })({
      ...req,
    });
  }

  public async listSecurityOptionTypes(req: ListSecurityOptionTypesRequest) {
    return await fetchWrapRPC(ConfigService_ListSecurityOptionTypes, {
      basePath: `${this.basePath}${DEPLOYER_PREFIX}`,
    })({
      ...req,
    });
  }

  public async discoverKubernetesConfigShortcuts(req: DiscoverKubernetesConfigShortcutsRequest) {
    return await fetchWrapRPC(ConfigService_DiscoverKubernetesConfigShortcuts, {
      basePath: `${this.basePath}${DEPLOYER_PREFIX}`,
    })({
      ...req,
    });
  }

  public async discoverDefaultRuntimeKubernetesResourceRequirements(
    req: DiscoverDefaultRuntimeKubernetesResourceRequirementsRequest
  ) {
    return await fetchWrapRPC(ConfigService_DiscoverDefaultRuntimeKubernetesResourceRequirements, {
      basePath: `${this.basePath}${DEPLOYER_PREFIX}`,
    })({
      ...req,
    });
  }

  public async generateDeploymentLogs(req: GenerateLogsRequest) {
    return await fetchWrapRPC(LogService_GenerateLogs, {
      basePath: `${this.basePath}${DEPLOYER_PREFIX}`,
    })({
      ...req,
    });
  }

  public async batchGetDeploymentStatuses(req: BatchGetDeploymentStatusesRequest) {
    return await fetchWrapRPC(DeploymentStatusService_BatchGetDeploymentStatuses, {
      basePath: `${this.basePath}${DEPLOYER_PREFIX}`,
    })({
      ...req,
    });
  }

  public async computeSuggestedScoringResourceRequirements(req: ComputeSuggestedScoringResourceRequirementsRequest) {
    return await fetchWrapRPC(DeploymentProfilingService_ComputeSuggestedScoringResourceRequirements, {
      basePath: `${this.basePath}${DEPLOYER_PREFIX}`,
    })({
      ...req,
    });
  }

  public async listExperimentArtifactCompositions(req: ListExperimentArtifactCompositionsRequest) {
    return await fetchWrapRPC(ArtifactCompositionService_ListExperimentArtifactCompositions, {
      basePath: `${this.basePath}${DEPLOYER_PREFIX}`,
    })({
      ...req,
    });
  }

  public async getDeploymentMetadata(req: GetDeploymentMetadataRequest) {
    return await fetchWrapRPC(DeploymentMetadataService_GetDeploymentMetadata, {
      basePath: `${this.basePath}${DEPLOYER_PREFIX}`,
    })({
      ...req,
    });
  }

  public async batchGetDeploymentMetadata(req: BatchGetDeploymentsMetadataRequest) {
    return await fetchWrapRPC(DeploymentMetadataService_BatchGetDeploymentsMetadata, {
      basePath: `${this.basePath}${DEPLOYER_PREFIX}`,
    })({
      ...req,
    });
  }
}
